/**
 * Join identical in-flight GET requests.
 *
 * React Strict Mode and several providers start the same read before the first
 * response arrives. Sharing that promise is safe: the request is idempotent,
 * the key includes the bearer token, and each caller receives its own data copy.
 * Successful responses are not cached here — TanStack Query owns cache lifetime
 * so a mutation can refetch immediately.
 */

function stableParams(params) {
  if (params == null) return "";
  if (typeof params !== "object") return String(params);
  const keys = Object.keys(params).sort();
  return keys.map((key) => `${key}=${params[key] ?? ""}`).join("&");
}

export function buildGetCoalesceKey(config) {
  const method = String(config?.method || "get").toLowerCase();
  const auth = config?.headers?.Authorization || config?.headers?.authorization || "";
  return [
    method,
    config?.baseURL || "",
    config?.url || "",
    stableParams(config?.params),
    auth,
  ].join("|");
}

function cloneData(data) {
  if (data == null || typeof data !== "object") return data;
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(data);
    } catch {
      /* fall through */
    }
  }
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
}

function cloneResponse(response) {
  if (!response || typeof response !== "object") return response;
  return { ...response, data: cloneData(response.data) };
}

export function createGetCoalescer(inflight = new Map()) {
  function clear() {
    inflight.clear();
  }

  function adapter(baseAdapter) {
    return async function coalescedAdapter(config) {
      const method = String(config?.method || "get").toLowerCase();
      if (method !== "get" || config?.brixigoFresh) {
        return baseAdapter(config);
      }
      const key = buildGetCoalesceKey(config);
      const existing = inflight.get(key);
      if (existing) {
        const response = await existing;
        return cloneResponse(response);
      }
      let settle;
      const shared = new Promise((resolve, reject) => {
        settle = { resolve, reject };
      });
      inflight.set(key, shared);
      baseAdapter(config).then(
        (response) => {
          inflight.delete(key);
          settle.resolve(response);
        },
        (error) => {
          inflight.delete(key);
          settle.reject(error);
        }
      );
      return shared;
    };
  }

  return { adapter, clear, inflight };
}

const singleton = createGetCoalescer();

export function coalesceGetAdapter(baseAdapter) {
  return singleton.adapter(baseAdapter);
}

export function clearGetCoalesce() {
  singleton.clear();
}
